import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  BankOutlined,
  TeamOutlined,
  HistoryOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { Button, message, Spin } from 'antd';
import { getOrganizationList, type OrganizationItem } from '../../api';

const OrganizationCard: React.FC<{
  organization: OrganizationItem;
  onView: (org: OrganizationItem) => void;
}> = ({ organization, onView }) => {
  return (
    <article className="group relative overflow-hidden rounded-xl bg-white p-5 shadow-[0_4px_12px_rgba(12,31,58,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(18,155,254,0.18)]">
      {/* 右上角装饰区 */}
      <span className="pointer-events-none absolute right-0 top-0 h-10 w-10 rounded-bl-[28px] bg-[#c8d8f0] transition-colors group-hover:bg-[#129bfe]" />

      <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-[#eaf1ff] text-xs text-[#129bfe]">
        <BankOutlined style={{ fontSize: '18px' }} />
      </div>

      <h3 className="mb-2 text-base font-semibold leading-tight text-[#101828] transition-colors group-hover:text-[#129bfe]">
        {organization.name}
      </h3>

      <div className="mb-1.5 flex items-center text-xs text-[#97a0af]">
        <TeamOutlined className="mr-2 text-[#c2cad6]" />
        <span className="mr-1">人员总数:</span>
        <span className="font-semibold text-[#374151]">
          {organization.atomicCount}
        </span>
      </div>

      <div className="flex items-center text-xs text-[#97a0af]">
        <HistoryOutlined className="mr-2 text-[#c2cad6]" />
        <span className="mr-1">创建时间:</span>
        <span className="font-semibold text-[#374151]">
          {organization.createTime}
        </span>
      </div>

      <Button
        type="primary"
        icon={<EyeOutlined />}
        className="!mt-5 !h-9 !w-full !rounded-lg !border-none !bg-[#0b1834] !text-xs !font-semibold hover:!bg-[#129bfe]"
        onClick={() => onView(organization)}
      >
        查看组织架构
      </Button>
    </article>
  );
};

const OrganizationManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  // 页面加载后拉取组织列表，用于组织管理页面展示
  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization-list'],
    queryFn: () => getOrganizationList(),
  });

  useEffect(() => {
    if (error instanceof Error) {
      messageApi.error(error.message);
    }
  }, [error, messageApi]);

  return (
    <div className="h-full min-h-0 overflow-auto px-6 py-7">
      {contextHolder}

      <div className="mx-auto w-full max-w-[1400px] 2xl:max-w-[1600px]">
        <div className="mb-7 flex items-start justify-between">
          <div>
            <h2 className="mb-1 text-xl font-bold leading-tight text-[#111827]">
              组织概览
            </h2>
            <p className="text-sm text-[#8b96a9]">
              管理并查看您已创建的所有组织架构。
            </p>
          </div>

          <div className="flex items-center pt-4">
            <div className="mr-3 rounded-lg bg-white px-3 py-1.5 text-sm text-[#6b7280]">
              共{' '}
              <span className="mx-1 font-semibold text-[#255bfa]">
                {data.length}
              </span>{' '}
              个组织
            </div>
            <Button
              icon={<PlusOutlined />}
              className="!h-9 !rounded-lg !border-none !bg-black !px-5 !text-sm !font-semibold !text-white hover:!bg-[#111827]"
              onClick={() => navigate('/organization/create')}
            >
              创建组织
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-60 items-center justify-center rounded-xl bg-white">
            <Spin size="large" />
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-5">
            {data.map(organization => (
              <OrganizationCard
                key={organization.id}
                organization={organization}
                onView={org => {
                  // 将组织信息存入 sessionStorage，供查看页面读取
                  sessionStorage.setItem(`org_${org.id}`, JSON.stringify(org));
                  navigate(`/organization/${org.id}`);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationManagementPage;
